import CorrectIcon from 'assets/svg/correct/feather-check.svg'
import Button from 'common/components/Button'
import TextInput from 'common/components/TextInput'
import Typography from 'common/components/Typography'
import { WEB_DESKTOP_RALALI_URL } from 'common/constants'
import theme from 'common/theme'
import validateEmailFormat from 'lib/common/is-email'
import { useLoadedFlag } from 'lib/feature-flags'
import {
  destroyLocalStorage,
  getLocalStorage,
} from 'lib/local-storage'
import validatePassword from 'lib/validator/validate-password'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SocialLogin from 'services/Auth/components/SocialLogin'
import QuestionnaireWrapper from 'services/Questionnaire/widget/QuestionnaireWrapper'
import { useStoreActions, useStoreState } from 'stores/hooks'
import styled from 'styled-components'

const RegisterContainer = styled.div`
  display: flex;
  align-self: center;
  flex-direction: column;
  width: 480px;
  padding: 50px;
  ${(props) =>
    props.responsive &&
    `
    @media (max-width: 1199.98px) { 
      padding: 16px;
      width: initial;
    }
  `}
`
const HeaderSection = styled.div``

const Title = styled(Typography)`
  @media (min-width: 768px) {
    font-size: ${theme.typography.bh3.fontSize};
  }
  @media (min-width: 1200px) {
    font-size: ${theme.typography.bh2.fontSize};
  }
`

const WrapperBody = styled.div`
  display: flex;
  justify-content: space-between;
`
const CheckboxContainer = styled.div`
  display: flex;
`
const Checkbox = styled.span`
  position: relative;

  input[type='checkbox'] {
    display: none;
  }
  input[type='checkbox'] + span {
    display: inline-block;
    position: relative;
    top: -1px;
    width: 16px;
    height: 16px;
    margin: -1px 0px 0 0;
    vertical-align: middle;
    background: white left top no-repeat;
    cursor: pointer;
    border: 0.5px solid ${theme.colors.gray};
    border-radius: 4px;
  }
  input[type='checkbox']:checked + span {
    border: 0.5px solid ${theme.colors.orange};
    background: ${theme.colors.orange} -19px top no-repeat;
  }
`
const Icon = styled.i`
  position: relative;
  bottom: 3px;
  left: 2px;
`
const BodySection = styled.div`
  margin-top: 30px;
`

const StyledLinkText = styled.a`
  text-decoration: none;
  color: ${theme.colors.orange};
  ${(props) => (props.bold ? 'font-weight: bold;' : '')}
`
const FooterSection = styled.div`
  margin-top: 30px;
  margin-bottom: 50px;
`
const ButtonNext = styled(Button)`
  font-size: 16px;
`

const CustomTextInput = (props) => {
  const handleChange = useCallback(
    (event) => props.onChange(event),
    [],
  )
  return <TextInput onChange={handleChange} {...props} />
}

const StyledTextInput = styled(CustomTextInput)`
  font-weight: bold;
  ::placeholder {
    font-weight: normal;
  }
  ${(props) =>
    props.responsive &&
    `
    @media (max-width: 1199.98px) { 
      width: 100%;
    }
  `}
`

interface IRegisterForm {
  responsive?: boolean
}

function RegisterForm({
  responsive = false,
}: IRegisterForm): JSX.Element {
  const { t } = useTranslation()
  const [fullName, setFullName] = useState<string>('')
  const [username, setUsername] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [passwordConfirmation, setPasswordConfirmation] = useState<
    string
  >('')
  const [agreement, setAgreement] = useState<boolean>(false)
  const [fullNameValidated, setFullNameValidated] = useState<boolean>(
    false,
  )
  const [usernameValidated, setUsernameValidated] = useState<boolean>(
    false,
  )
  const [passwordValidated, setPasswordValidated] = useState<boolean>(
    false,
  )
  const [
    passwordConfirmationValidated,
    setPasswordConfirmationValidated,
  ] = useState<boolean>(false)
  const [isPhoneNumber, setIsPhoneNumber] = useState<boolean>(false)
  const [isEmail, setIsEmail] = useState<boolean>(false)
  const [isValidated, setIsValidated] = useState<boolean>(false)
  const [focusField, setFocusField] = useState<string>('')
  const [passwordHelperText, setPasswordHelperText] = useState<
    string
  >('')

  const { getOtpChannel } = useStoreActions(
    (actions) => actions.register,
  )
  const { answerList } = useStoreState((state) => state.questionnaire)

  const websiteUserProfilingGlobalFeature = useLoadedFlag(
    'biz--questionnaire--user-profiling--website',
  )

  const validatePasswordSecurity = (
    passwordValue: string,
    fullNameValue: string,
    usernameValue: string,
  ) => {
    const trimPasswordValue = passwordValue.trim()
    const trimFullName = fullNameValue.trim()
    const isSecure = !(
      new RegExp(trimPasswordValue).test(trimFullName) ||
      new RegExp(trimPasswordValue).test(usernameValue)
    )
    return isSecure
  }

  const handleValidatePassword = (
    passwordValue,
    passwordConfirmationValue,
    fullNameValue?: string,
    userNameValue?: string,
  ) => {
    const isSecure = validatePasswordSecurity(
      passwordValue,
      fullNameValue || fullName,
      userNameValue || username,
    )

    const passwordValidation = validatePassword(passwordValue)
    if (!isSecure) {
      setPasswordHelperText(
        'Kata sandi tidak boleh mengandung email ataupun nama Anda',
      )
    } else if (
      !passwordValidation.isLengthValidate ||
      !passwordValidation.isRegexValidate
    ) {
      setPasswordHelperText(
        'Min. 8 - 50 karakter terdiri dari huruf kecil besar, angka, dan simbol',
      )
    }
    const isPasswordValidated =
      passwordValidation.isLengthValidate &&
      passwordValidation.isRegexValidate &&
      isSecure
    setPasswordValidated(isPasswordValidated)
    const isPasswordConfirmationValidated =
      passwordValue === passwordConfirmationValue
    setPasswordConfirmationValidated(isPasswordConfirmationValidated)
  }

  const validateFullName = (fullNameValue) => {
    const regexName = /^[-0-9a-zA-Z-.,' ]+$/

    if (
      regexName.test(fullNameValue) &&
      fullNameValue.length >= 3 &&
      fullNameValue.length <= 100
    ) {
      setFullNameValidated(true)
      handleValidatePassword(
        password,
        passwordConfirmation,
        fullNameValue,
      )
    } else {
      setFullNameValidated(false)
    }
  }

  const validateUsername = (usernameValue) => {
    const regexNumber = /^[0-9\b]{9,15}$/

    if (regexNumber.test(usernameValue)) {
      setIsPhoneNumber(true)
      setIsEmail(false)
      setUsernameValidated(true)
    } else if (validateEmailFormat(usernameValue, 'alphaFirst')) {
      setIsPhoneNumber(false)
      setIsEmail(true)
      setUsernameValidated(true)
      handleValidatePassword(
        password,
        passwordConfirmation,
        usernameValue,
      )
    } else {
      setIsEmail(false)
      setIsPhoneNumber(false)
      setUsernameValidated(false)
    }
  }

  const fullNameInputHandler = useCallback(
    (event) => {
      validateFullName(event.target.value)
      setFullName(event.target.value)
    },
    [fullName, username, password, passwordConfirmation],
  )

  const usernameInputHandler = useCallback(
    (event) => {
      validateUsername(event.target.value)
      setUsername(event.target.value)
    },
    [fullName, username, password, passwordConfirmation],
  )

  const passwordInputHandler = useCallback(
    (event) => {
      handleValidatePassword(event.target.value, passwordConfirmation)
      setPassword(event.target.value)
    },
    [fullName, username, password, passwordConfirmation],
  )

  const passwordConfirmationInputHandler = useCallback(
    (event) => {
      handleValidatePassword(password, event.target.value)
      setPasswordConfirmation(event.target.value)
    },
    [fullName, username, password, passwordConfirmation],
  )

  const agreementHandler = () => {
    setAgreement(!agreement)
  }

  useEffect(() => {
    // checking questionnaire if there is null/blank answer
    let questionnaireValidate = answerList?.filter(
      (ans) => !ans.answer,
    )
    if (!websiteUserProfilingGlobalFeature) {
      questionnaireValidate = []
    }
    if (!isEmail) {
      setIsValidated(
        fullNameValidated &&
          usernameValidated &&
          agreement &&
          questionnaireValidate?.length < 1,
      )
    } else {
      setIsValidated(
        fullNameValidated &&
          usernameValidated &&
          passwordValidated &&
          passwordConfirmationValidated &&
          agreement &&
          questionnaireValidate?.length < 1,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fullNameValidated,
    usernameValidated,
    passwordValidated,
    passwordConfirmationValidated,
    agreement,
    answerList,
    websiteUserProfilingGlobalFeature,
  ])

  const formSubmissionHandler = async (event) => {
    event.preventDefault()
    if (isValidated) {
      getOtpChannel({
        fullName,
        username,
        password,
        isEmail,
      })
    }
  }

  useEffect(() => {
    if (getLocalStorage('username_register')) {
      validateUsername(String(getLocalStorage('username_register')))
      setUsername(String(getLocalStorage('username_register')))
      destroyLocalStorage('username_register')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isFullNameValidated = () => {
    return fullName && !fullNameValidated
  }

  const isUsernameValidated = () => {
    return username && !usernameValidated
  }

  const isPasswordValidated = () => {
    return password && !passwordValidated
  }

  const isPasswordConfirmValidated = () => {
    return passwordConfirmation && !passwordConfirmationValidated
  }

  const inputMargin = (condition: boolean) => {
    return condition ? '0 0 10px' : '0 0 20px'
  }

  const handleFocusFieldPassword = useCallback(
    () => setFocusField('password'),
    [],
  )

  const handleBlurFieldPassword = useCallback(
    () => setFocusField(''),
    [],
  )
  return (
    <RegisterContainer
      responsive={responsive}
      data-testid="register-test"
    >
      <HeaderSection>
        <Title type="bh4" textAlign="center">
          {t('SIGNUP_RALALI', { ns: 'auth' })}
        </Title>
        <Typography type="rh6" textAlign="center" marginTop="15px">
          <>
            {t('ALREADY_SIGNUP', { ns: 'auth' })}
            <StyledLinkText href="/login" bold>
              {` ${t('SIGNIN', { ns: 'auth' })}`}
            </StyledLinkText>
          </>
        </Typography>
        <SocialLogin />
      </HeaderSection>
      <form onSubmit={formSubmissionHandler}>
        <BodySection>
          {websiteUserProfilingGlobalFeature && (
            <QuestionnaireWrapper responsive={responsive} />
          )}
          <StyledTextInput
            id="fullname"
            width="480px"
            onChange={fullNameInputHandler}
            floatingLabel={t('FULLNAME', { ns: 'auth' })}
            data-testid="input-fullname"
            value={fullName}
            placeholder={
              focusField === 'fullname'
                ? t('EXAMPLE_NAME', { ns: 'auth' })
                : ''
            }
            onFocus={useCallback(() => setFocusField('fullname'), [])}
            onBlur={useCallback(() => setFocusField(''), [])}
            helperText={
              isFullNameValidated() &&
              t('INVALID_NAME', { ns: 'auth' })
            }
            hideHelperText={!isFullNameValidated()}
            margin={inputMargin(isFullNameValidated())}
            error={isFullNameValidated()}
            responsive={responsive}
          />
          <StyledTextInput
            id="username"
            width="480px"
            onChange={usernameInputHandler}
            floatingLabel={t('EMAIL_PHONE', { ns: 'auth' })}
            data-testid="input-username"
            value={username}
            placeholder={
              focusField === 'username'
                ? t('EXAMPLE_EMAIL_PHONE', { ns: 'auth' })
                : ''
            }
            onFocus={useCallback(() => setFocusField('username'), [])}
            onBlur={useCallback(() => setFocusField(''), [])}
            helperText={
              isUsernameValidated() &&
              t('INVALID_EMAIL_PHONE', { ns: 'auth' })
            }
            hideHelperText={!isUsernameValidated()}
            margin={inputMargin(isUsernameValidated())}
            error={isUsernameValidated()}
            responsive={responsive}
          />
          {!isPhoneNumber && isEmail && (
            <>
              <StyledTextInput
                isVisibilityToggleable
                floatingLabel={t('PASSWORD', { ns: 'auth' })}
                width="480px"
                onChange={passwordInputHandler}
                data-testid="input-password"
                value={password}
                placeholder={
                  focusField === 'password'
                    ? t('INVALID_PASSWORD', { ns: 'auth' })
                    : ''
                }
                onFocus={handleFocusFieldPassword}
                onBlur={handleBlurFieldPassword}
                helperText={
                  isPasswordValidated() && passwordHelperText
                }
                hideHelperText={!isPasswordValidated()}
                margin={
                  isPasswordValidated() ? '0 0 10px' : '0 0 20px'
                }
                error={isPasswordValidated()}
                responsive={responsive}
              />
              <StyledTextInput
                isVisibilityToggleable
                floatingLabel={t('REPEAT_PASSWORD', { ns: 'auth' })}
                width="480px"
                onChange={passwordConfirmationInputHandler}
                data-testid="input-password-confirmation"
                value={passwordConfirmation}
                placeholder={
                  focusField === 'password-confirmation'
                    ? t('INVALID_PASSWORD', { ns: 'auth' })
                    : ''
                }
                onFocus={() => setFocusField('password-confirmation')}
                onBlur={() => setFocusField('')}
                helperText={
                  isPasswordConfirmValidated() &&
                  t('INVALID_CONFIRM_PASSWORD', { ns: 'auth' })
                }
                hideHelperText={!isPasswordConfirmValidated()}
                margin={
                  isPasswordConfirmValidated()
                    ? '0 0 10px'
                    : '0 0 20px'
                }
                error={isPasswordConfirmValidated()}
                responsive={responsive}
              />
            </>
          )}
          <WrapperBody>
            <CheckboxContainer>
              <Checkbox
                onClick={agreementHandler}
                data-testid="checkbox-agreement"
              >
                <input type="checkbox" checked={agreement} readOnly />
                <span>
                  {agreement && (
                    <Icon>
                      <CorrectIcon />
                    </Icon>
                  )}
                </span>
              </Checkbox>
              <Typography marginLeft="10px">
                <>
                  {t('SIGNUP_AGGREE', { ns: 'auth' })}
                  <StyledLinkText
                    href={`${WEB_DESKTOP_RALALI_URL}/syaratdanketentuan`}
                    target="_blank"
                  >
                    {` ${t('TERMS_CONDITION', { ns: 'auth' })} `}
                  </StyledLinkText>
                  {t('AND', { ns: 'auth' })}
                  <StyledLinkText
                    href={`${WEB_DESKTOP_RALALI_URL}/kebijakanprivasi`}
                    target="_blank"
                  >
                    {` ${t('PRIVACY_POLICY', { ns: 'auth' })}`}
                  </StyledLinkText>
                </>
              </Typography>
            </CheckboxContainer>
          </WrapperBody>
        </BodySection>
        <FooterSection>
          <ButtonNext
            type="submit"
            size="large"
            block
            disabled={!isValidated}
            data-testid="button-submit"
            fontWeight="bold"
          >
            {t('SIGNUP', { ns: 'auth' })}
          </ButtonNext>
        </FooterSection>
      </form>
    </RegisterContainer>
  )
}

export default RegisterForm
